import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import {
  LayoutDashboard, MessageSquare, Key, Settings, Activity,
  LogOut, ChevronLeft, ChevronRight, Wifi, Send, FileText
} from 'lucide-react';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/whatsapp', icon: Wifi, label: 'WhatsApp' },
  { path: '/messages', icon: MessageSquare, label: 'Messages' },
  { path: '/send', icon: Send, label: 'Send Message' },
  { path: '/api-keys', icon: Key, label: 'API Keys' },
  { path: '/logs', icon: FileText, label: 'Logs' },
  { path: '/config', icon: Settings, label: 'Configuration' },
  { path: '/health', icon: Activity, label: 'System Health' },
];

export default function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const { admin, logout } = useAuth();
  const { whatsappStatus, connected } = useSocket();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isWAConnected = whatsappStatus ? whatsappStatus.connected : false;

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#0f172a' }}>
      {/* Sidebar */}
      <div style={{
        width: collapsed ? 64 : 220,
        background: '#1e293b',
        borderRight: '1px solid #334155',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s',
        flexShrink: 0,
        overflow: 'hidden',
      }}>
        <div style={{ padding: '16px 14px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22, flexShrink: 0 }}>💬</span>
          {!collapsed && <span style={{ fontSize: 14, fontWeight: 700, color: '#25d366', whiteSpace: 'nowrap' }}>WA Admin</span>}
        </div>

        <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
          {navItems.map(({ path, icon: Icon, label }) => {
            const active = location.pathname === path;
            return (
              <Link
                key={path}
                to={path}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 14px',
                  color: active ? '#25d366' : '#94a3b8',
                  background: active ? 'rgba(37,211,102,0.1)' : 'transparent',
                  borderLeft: '3px solid ' + (active ? '#25d366' : 'transparent'),
                  textDecoration: 'none',
                  fontSize: 14,
                  fontWeight: active ? 600 : 400,
                  transition: 'all 0.15s',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                <Icon size={18} style={{ flexShrink: 0 }} />
                {!collapsed && <span>{label}</span>}
              </Link>
            );
          })}
        </nav>

        <div style={{ borderTop: '1px solid #334155', padding: 8 }}>
          <button
            onClick={() => setCollapsed(!collapsed)}
            style={{ width: '100%', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '8px 6px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
          >
            {collapsed ? <ChevronRight size={16} /> : <><ChevronLeft size={16} /><span>Collapse</span></>}
          </button>
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ background: '#1e293b', borderBottom: '1px solid #334155', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <span style={{ color: '#64748b', fontSize: 13 }}>
              WhatsApp:{' '}
              <span style={{ color: isWAConnected ? '#25d366' : '#ef4444', fontWeight: 600 }}>
                {isWAConnected ? '● Connected' : '● Disconnected'}
              </span>
            </span>
            <span style={{ color: '#64748b', fontSize: 13 }}>
              Socket:{' '}
              <span style={{ color: connected ? '#25d366' : '#ef4444' }}>
                {connected ? '● Live' : '● Offline'}
              </span>
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#25d366', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#000' }}>
              {admin && admin.username ? admin.username[0].toUpperCase() : 'A'}
            </div>
            <span style={{ color: '#e2e8f0', fontSize: 14 }}>{admin ? admin.username : ''}</span>
            <button
              onClick={handleLogout}
              style={{ background: 'none', border: '1px solid #334155', color: '#94a3b8', cursor: 'pointer', padding: '6px 12px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {children}
        </div>
      </div>
    </div>
  );
}
